import { useState } from "react";

import dayjs from "dayjs";

import { FaEllipsisV } from "react-icons/fa";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";

import { identifierOrNationalHealthCard } from "@/utils/identifierOrNationalHealthCard";
import { BasePatient } from "@/@types/BasePatient";
import { Paginated } from "@/@types/generics/paginated";

import { TableSkeletons } from "@/components/TableSkeletons";
import { ViewCitizenButton } from "@/components/ViewCitizenButton";
import { PremilinaryReportStatusIcon } from "@/components/PremilinaryReportStatusIcon";
import { CitizenSyncLabel } from "@/components/CitizenSyncLabel";
import { ModalIndicator5 } from "../ModalIndicator5";

import "./styles.scss";

type Props = {
	patient: Paginated<BasePatient>;
	isLoading: boolean;
};

type Column = {
	title: string;
	visible: boolean;
};

type ColumnsTable = {
	sinc: Column;
	name: Column;
	motherName: Column;
	birthdate: Column;
	identifier: Column;
	healthTeam: Column;
	mc: Column;
	age: Column;
};

export function TableIndicator5({ isLoading, patient }: Props) {
	const [patientSelected, setPatientSelected] = useState<BasePatient>({} as BasePatient);
	const [showModalIndicator5, setShowModalIndicator5] = useState(false);
	const handleShowModalIndicator5 = () => {
		setShowModalIndicator5(true);
	};
	const handleCloseModalIndicator5 = () => {
		setShowModalIndicator5(false);
	};

	const [columns, setColumns] = useState<ColumnsTable>({
		sinc: { title: "Sinc", visible: false },
		name: { title: "Nome", visible: true },
		motherName: { title: "Nome da mãe", visible: true },
		birthdate: { title: "DN", visible: true },
		identifier: { title: "CPF/CNS", visible: false },
		healthTeam: { title: "Equipe", visible: false },
		mc: { title: "MC", visible: false },
		age: { title: "Idade", visible: true },
	});

	const handleColumnToggle = (column: keyof ColumnsTable) => {
		setColumns((prevColumns) => ({
			...prevColumns,
			[column]: {
				...prevColumns[column],
				visible: !prevColumns[column].visible,
			},
		}));
	};

	return (
		<div>
			<ModalIndicator5
				show={showModalIndicator5}
				onHide={handleCloseModalIndicator5}
				patientSelected={patientSelected}
			/>
			<Table responsive className="table-striped-custom table-rounded border gs-3">
				<thead>
					<tr className="fw-bold fs-6 text-center text-gray-800 text-uppercase border-bottom border-gray-200">
						{columns.sinc.visible && (
							<th>
								<CitizenSyncLabel />
							</th>
						)}
						{columns.name.visible && <th>CIDADÃO</th>}
						{columns.motherName.visible && <th>NOME DA MÃE</th>}
						{columns.birthdate.visible && <th>DN</th>}
						{columns.identifier.visible && <th>CPF/CNS</th>}
						{columns.healthTeam.visible && <th>EQUIPE</th>}
						{columns.mc.visible && <th>MC</th>}
						{columns.age.visible && <th>IDADE</th>}
						<th className="d-flex align-items-baseline justify-content-between text-nowrap pb-0 no-print no-excel">
							<label>AÇÕES</label>
							<OverlayTrigger
								trigger="click"
								placement="bottom"
								overlay={
									<Popover>
										<Popover.Body>
											<Form className="d-flex flex-column gap-2">
												{Object.keys(columns).map((key, index) => (
													<Form.Check
														key={`column-${index}`}
														type="checkbox"
														label={
															columns[key as keyof ColumnsTable].title
														}
														checked={
															columns[key as keyof ColumnsTable]
																.visible
														}
														onChange={() =>
															handleColumnToggle(
																key as keyof ColumnsTable
															)
														}
													/>
												))}
											</Form>
										</Popover.Body>
									</Popover>
								}
								rootClose
							>
								<Button
									title="Exibição de colunas da tabela"
									variant="white"
									className="px-2"
									disabled={isLoading}
								>
									<FaEllipsisV />
								</Button>
							</OverlayTrigger>
						</th>
					</tr>
				</thead>
				<tbody>
					{!isLoading && patient?.data ? (
						patient?.data?.map((patient, index) => (
							<tr key={`patient-pregnant-${index}`}>
								{columns.sinc.visible && (
									<td>
										<PremilinaryReportStatusIcon
											status={patient.preliminaryReportStatus}
										/>
									</td>
								)}
								{columns.name.visible && (
									<td className="text-nowrap">{patient.fullName}</td>
								)}
								{columns.motherName.visible && (
									<td className="text-nowrap text-center">
										{patient.motherName?.toUpperCase() || "N/A"}
									</td>
								)}
								{columns.birthdate.visible && (
									<td className="text-nowrap text-center">
										{patient.birthdate
											? dayjs(patient.birthdate).format("DD/MM/YYYY")
											: "N/A"}
									</td>
								)}
								{columns.identifier.visible && (
									<td className="text-nowrap text-center">
										{identifierOrNationalHealthCard(patient)}
									</td>
								)}
								{columns.healthTeam.visible && (
									<td className="text-nowrap text-center">
										{patient.healthTeam?.name || "N/A"}
									</td>
								)}
								{columns.mc.visible && (
									<td className="text-nowrap text-center text-center">
										{patient.area || "N/A"}
									</td>
								)}
								{columns.age.visible && (
									<td className="text-nowrap text-center">{patient.age}</td>
								)}
								<td className="text-center no-print">
									<ViewCitizenButton
										onClick={() => {
											setPatientSelected(patient);
											handleShowModalIndicator5();
										}}
									/>
								</td>
							</tr>
						))
					) : (
						<TableSkeletons
							numberOfCells={
								Object.entries(columns).filter((a) => a[1]?.visible === true)
									.length + 1
							}
							height={20}
							numberOfRows={10}
						/>
					)}
				</tbody>
			</Table>
		</div>
	);
}
